import {get, isEmpty} from 'lodash'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useIntl} from 'react-intl'
import {convertUnit, round} from '../../../../../_gori/helpers'
import {useStatesGlobal} from '../../../../../_gori/helpers/components/StatesGlobalProvider'
import {
  FEDEX_INTERNATIONAL_SERVICES,
  FEDEX_PARCEL_LIMIT,
  OPTION_PACKAGE_TYPE,
  UNIT_VALUES,
} from '../../core/_const'
import {ParcelItem} from './PackageType'
import {WEIGHT_UNIT, DIMENSION_UNIT} from '../../../../../_gori/constants'
import OrderHelper from '../../core/_helpers'

type UsePackageInfoParams = {
  formik: any
  disabled?: boolean
  hasGetPackage?: boolean
  handleAddNewItem?: (section: string) => void
  handleDeleteItem?: (section: string, key: string) => void
  setCurrentDimensionUnit?: (unit: string) => void
  setCurrentWeightUnit?: (unit: string) => void
  isDisabledAddPackage?: boolean
}

export function usePackageInfo({
  formik,
  disabled,
  hasGetPackage = true,
  handleAddNewItem,
  handleDeleteItem,
  setCurrentDimensionUnit,
  setCurrentWeightUnit,
  isDisabledAddPackage = false,
}: UsePackageInfoParams) {
  const intl = useIntl()
  const {statesGlobal, getPackages} = useStatesGlobal()
  const orderHelper = OrderHelper()
  const packageTypePlaceholder = intl.formatMessage({id: 'PACKAGE_TYPE'})

  const [labelPackageType, setLabelPackageType] = useState<string | undefined>(
    packageTypePlaceholder
  )
  const [isDisabledPackageCount, setIsDisabledPackageCount] = useState(false)
  const [oneRateUnmatched, setOneRateUnmatched] = useState(false)

  const carriersPackage = statesGlobal?.carriersPackage
  const packages = statesGlobal.packages

  // ---------------------------------------------------------------------------
  // Derived values
  // ---------------------------------------------------------------------------

  const parcelsCount = Object.keys(formik.values.parcels || {}).length

  const reachLimitRow = useMemo(
    () => parcelsCount >= UNIT_VALUES.PACKAGE_COUNT.limit_row,
    [parcelsCount]
  )

  // ---------------------------------------------------------------------------
  // One-rate helpers
  // ---------------------------------------------------------------------------

  const oneRateItems = useMemo(() => {
    const group = carriersPackage?.find((g: any) => g.label === 'FedEx One Rate Package')
    return group?.value || []
  }, [carriersPackage])

  const isOneRatePackage = useMemo(() => {
    const parcels = formik.values.parcels || {}
    const firstKey = Object.keys(parcels)?.[0]
    const first = parcels?.[firstKey]
    if (!first?.package_type) return false
    return oneRateItems.some((item: any) => item.value === first.package_type)
  }, [formik.values.parcels, oneRateItems])

  // Tracks which package_type values have already had weight_limit backfilled.
  // Using a ref (not state) means the write never causes a re-render or re-triggers
  // the effect, breaking the self-trigger loop that formik.values.parcels as a dep
  // would otherwise create (effect writes → dep changes → effect fires again).
  // The set is cleared when oneRateItems reloads so a carrier-package refresh
  // correctly re-backfills.
  const backfilledPackageTypes = useRef<Set<string>>(new Set())

  useEffect(() => {
    // Reset the backfill registry whenever the carrier package list reloads
    // so stale entries don't block a fresh backfill after a data refresh.
    backfilledPackageTypes.current.clear()
  }, [oneRateItems])

  // When an order loads with a One Rate package already selected, the parcel
  // data from the API doesn't include weight_limit (it was never persisted).
  // Back-fill it from oneRateItems so weightLimitError can compute correctly.
  useEffect(() => {
    if (!isOneRatePackage || !oneRateItems.length) return
    const parcels = formik.values.parcels || {}
    const firstKey = Object.keys(parcels)?.[0]
    if (!firstKey) return
    const parcel = parcels[firstKey]
    const packageType = parcel?.package_type
    if (!packageType) return

    // Already backfilled for this package type — skip to avoid self-triggering.
    // (setFieldValue writes back into formik.values.parcels which is a dep here;
    // the ref flag stops the second cycle without relying on the value being set.)
    if (backfilledPackageTypes.current.has(packageType)) return

    // If weight_limit is already present (e.g. user just picked from the dropdown),
    // record it as done and skip the write.
    if (parcel?.weight_limit) {
      backfilledPackageTypes.current.add(packageType)
      return
    }

    const matched = oneRateItems.find((item: any) => item.value === packageType)
    if (!matched?.parcel?.weight_limit) return

    backfilledPackageTypes.current.add(packageType)
    formik.setFieldValue(`parcels.${firstKey}.weight_limit`, matched.parcel.weight_limit)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOneRatePackage, oneRateItems, formik.values.parcels])

  const weightLimitError = useMemo(() => {
    if (!isOneRatePackage) return null

    const parcels = formik.values.parcels || {}
    const firstKey = Object.keys(parcels)?.[0]
    const parcel = parcels?.[firstKey]
    if (!parcel) return null

    // weight_limit is stored in oz (Weight::BASE) — e.g. 160 oz = 10 lb for Envelope,
    // 800 oz = 50 lb for all other One Rate types. The / 16 below converts to lbs for display.
    const weightLimit = Number(parcel.weight_limit) // oz
    if (!weightLimit) return null

    const enteredWeight = Number(parcel.weight) || 0
    const weightUnit = parcel.weight_unit || WEIGHT_UNIT.OZ

    const weightInOz = convertUnit({value: enteredWeight, unit: weightUnit}, {unit: WEIGHT_UNIT.OZ})

    if (weightInOz > weightLimit) {
      const lbsLimit = Math.round(weightLimit / 16) // convert oz → lbs for the error message
      // Look up the human-readable package label (e.g. "FedEx Envelope") from the One Rate
      // item list so the error names the specific package rather than the generic
      // "FedEx One Rate packages" phrasing.
      const matchedItem = oneRateItems.find((item: any) => item.value === parcel.package_type)
      const packageName = matchedItem?.label ?? 'FedEx One Rate'
      return intl.formatMessage({id: 'FEDEX_ONE_RATE_WEIGHT_LIMIT'}, {lbsLimit, packageName})
    }

    return null
  }, [intl, isOneRatePackage, oneRateItems, formik.values.parcels])

  useEffect(() => {
    const firstKey = Object.keys(formik.values.parcels || {})?.[0]
    if (!firstKey) return

    if (weightLimitError) {
      // Mark the field as touched so formik's is-invalid class fires and outlines
      // the input in red, matching the styling of other required-field errors.
      formik.setFieldTouched(`parcels.${firstKey}.weight`, true, false)
      formik.setFieldError(`parcels.${firstKey}.weight`, weightLimitError)
    } else {
      // Weight dropped back below limit — clear the field error and touched state
      // so the red outline goes away after the user corrects the weight.
      formik.setFieldTouched(`parcels.${firstKey}.weight`, false, false)
      formik.setFieldError(`parcels.${firstKey}.weight`, undefined)
    }
    // formik.setFieldError is not referentially stable; omitting `formik` from
    // the dep array is intentional — this effect should only re-run when
    // weightLimitError changes, not on every formik state update.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weightLimitError])

  const findBestFitOneRate = useCallback(
    (userL: number, userW: number, userH: number, dimUnit: string) => {
      const toInches = (val: number, unit: string) => {
        if (unit === DIMENSION_UNIT.IN) return val
        return convertUnit({value: val, unit}, {unit: DIMENSION_UNIT.IN})
      }

      // Sort ascending so index 0 = shortest side (thickness for flat packages)
      const [d1, d2, d3] = [
        toInches(userL, dimUnit),
        toInches(userW, dimUnit),
        toInches(userH, dimUnit),
      ].sort((a, b) => a - b)

      // All dimensions must be positive to match a package
      if (d1 <= 0) return null

      const userVolume = d1 * d2 * d3

      const round2 = (n: number) => Math.round(n * 100) / 100

      // Two-pass selection: flat packages (Envelope, Pak) are tried first.
      // If any flat package fits, we return the tightest-fitting one without
      // considering boxes. Only when no flat package fits do we fall through to
      // boxes.
      let bestFlat: any = null
      let bestFlatVolume = Infinity

      for (const item of oneRateItems) {
        const maxVolume = Number(item.parcel?.max_volume || 0)
        const maxHeight = item.parcel?.max_height != null ? Number(item.parcel.max_height) : null

        if (maxHeight === null) continue // skip boxes in this pass

        // Volume must fit
        if (userVolume > maxVolume) continue

        // Flat package (Envelope, Pak): shortest side must not exceed max_height
        if (d1 > maxHeight) continue

        // Footprint check: the two larger user dims must fit within the physical
        // package footprint in any orientation. User dims are sorted ascending so
        // d2 <= d3 and footprint sorted ascending gives [fpMin, fpMax].
        // (A) d3 > fpMax: the longest user dim exceeds even the larger footprint
        //     edge — no orientation works, reject.
        // (B) d2 > fpMin && d3 > fpMin: both user footprint dims exceed the
        //     shorter footprint edge, so neither can fit along it, reject.
        // Passing case: d2 <= fpMin — the mid user dim fits along the short
        //     footprint edge, so the content lies along the long axis. d3 <= fpMax
        //     is guaranteed by (A) not firing, so this orientation is valid.
        const [fpMin, fpMax] = [
          round2(Number(item.parcel?.length || 0)),
          round2(Number(item.parcel?.width || 0)),
        ].sort((a, b) => a - b)
        if (d3 > fpMax) continue
        if (d2 > fpMin && d3 > fpMin) continue

        if (maxVolume < bestFlatVolume) {
          bestFlatVolume = maxVolume
          bestFlat = item
        }
      }

      // A flat package matched — return it without checking boxes.
      if (bestFlat) return bestFlat

      // No flat package fits — try boxes.
      let bestBox: any = null
      let bestBoxVolume = Infinity

      for (const item of oneRateItems) {
        const maxVolume = Number(item.parcel?.max_volume || 0)
        const maxHeight = item.parcel?.max_height != null ? Number(item.parcel.max_height) : null

        if (maxHeight !== null) continue // skip flat packages in this pass

        // Volume must fit
        if (userVolume > maxVolume) continue

        // Volume is the only gate for boxes — this mirrors gori-api-v2's
        // validateFedExPackage which checks only volume + weight for all box types.
        if (maxVolume < bestBoxVolume) {
          bestBoxVolume = maxVolume
          bestBox = item
        }
      }

      return bestBox
    },
    [oneRateItems]
  )

  // ---------------------------------------------------------------------------
  // Disabled-state logic
  // ---------------------------------------------------------------------------

  const isDisabledDimension = useMemo(() => {
    if (disabled) return true
    if (oneRateUnmatched) return false
    if (parcelsCount > 1) return false
    if (labelPackageType === packageTypePlaceholder) return false

    const parcels = formik.values.parcels || {}
    const firstKey = Object.keys(parcels)?.[0]
    if (parcels?.[firstKey]?.package_id) return false

    return !(
      labelPackageType === OPTION_PACKAGE_TYPE.INPUT_DIMENSION.label ||
      labelPackageType === OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label ||
      labelPackageType?.toLowerCase().includes('cubic') ||
      isOneRatePackage
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    disabled,
    labelPackageType,
    parcelsCount,
    isOneRatePackage,
    oneRateUnmatched,
    packageTypePlaceholder,
  ])

  useEffect(() => {
    // Only auto-reset oneRateUnmatched when the user explicitly selects a non-one-rate package.
    // When oneRateUnmatched is true, package_type is cleared which makes isOneRatePackage false,
    // but we need the banner to persist until the user makes a new selection.
    if (!isOneRatePackage && !oneRateUnmatched) setOneRateUnmatched(false)
  }, [isOneRatePackage, oneRateUnmatched])

  useEffect(() => {
    const parcels = formik.values.parcels || {}
    const firstKey = Object.keys(parcels)?.[0]
    const first = parcels?.[firstKey]

    const flag =
      isDisabledDimension ||
      ((labelPackageType?.toLowerCase().includes('cubic') && first?.package_type) ?? false)

    setIsDisabledPackageCount(flag)
  }, [formik.values.parcels, labelPackageType, isDisabledDimension])

  // ---------------------------------------------------------------------------
  // Unit initialisation
  // ---------------------------------------------------------------------------

  useEffect(() => {
    const parcels = formik.values.parcels
    const firstParcel = Object.values(parcels)?.[0] as ParcelItem
    if (!firstParcel) return

    const weightUnit = firstParcel.weight_unit || WEIGHT_UNIT.OZ
    const dimensionUnit = firstParcel.dimension_unit || DIMENSION_UNIT.IN

    setCurrentWeightUnit?.(weightUnit)
    setCurrentDimensionUnit?.(dimensionUnit)

    formik.setFieldValue('weight_unit', weightUnit)
    formik.setFieldValue('dimension_unit', dimensionUnit)

    if (!firstParcel.weight_unit || !firstParcel.dimension_unit) {
      Object.keys(parcels).forEach((key) => {
        formik.setFieldValue(`parcels.${key}.weight_unit`, weightUnit)
        formik.setFieldValue(`parcels.${key}.dimension_unit`, dimensionUnit)
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.parcels])

  // ---------------------------------------------------------------------------
  // Totals & validation side-effects
  // ---------------------------------------------------------------------------

  const totalWeightPackage = useMemo(() => {
    let sum = 0
    Object.values(formik.values.parcels).forEach((value: any) => {
      sum +=
        (Number.parseFloat(value?.weight) || 0) * (Number.parseFloat(value?.package_count) || 1)
    })
    return round(sum)
  }, [formik.values.parcels])

  const totalWeightCustoms = useMemo(() => {
    let sum = 0
    Object.values(formik?.values?.custom_items || {}).forEach((value: any) => {
      sum += Number.parseFloat(value?.weight) || 0
    })
    return round(sum)
  }, [formik.values.custom_items])

  useEffect(() => {
    if (!formik.values.custom_items || !Object.keys(formik.values.parcels).length) return
    const weight_unit = get(Object.values(formik.values.parcels), '[0].weight_unit', '')
    if (!weight_unit) return

    const converted = convertUnit(
      {unit: WEIGHT_UNIT.OZ, value: totalWeightCustoms},
      {unit: weight_unit}
    )
    formik.setFieldValue('check_weight', converted > totalWeightPackage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.parcels, formik.values.custom_items, totalWeightCustoms, totalWeightPackage])

  const totalPackageCount = useMemo(() => {
    let sum = 0
    Object.values(formik.values.parcels).forEach((value: any) => {
      sum += Number.parseFloat(value?.package_count) || 1
    })
    return sum
  }, [formik.values.parcels])

  useEffect(() => {
    const isFedex = FEDEX_INTERNATIONAL_SERVICES.includes(formik.values.service)
    const maxAllowed = isFedex ? FEDEX_PARCEL_LIMIT : UNIT_VALUES.PACKAGE_COUNT.max

    let hasRowExceedingLimit = false
    if (isFedex) {
      Object.values(formik.values.parcels).forEach((value: any) => {
        const packageCountItem = Number.parseFloat(value?.package_count) || 1
        if (packageCountItem > FEDEX_PARCEL_LIMIT) {
          hasRowExceedingLimit = true
        }
      })
    }

    formik.setFieldValue(
      'check_package_count',
      totalPackageCount > maxAllowed || hasRowExceedingLimit
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPackageCount, formik.values.service])

  const totalPackages = useMemo(() => {
    let sum = 0
    Object.values(formik.values.parcels).forEach((value: any) => {
      sum += Number.parseFloat(value?.package_count) || 0
    })
    return sum
  }, [formik.values.parcels])

  // ---------------------------------------------------------------------------
  // Package-type options
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (hasGetPackage) getPackages()
  }, [getPackages, hasGetPackage])

  const optionsCustomPackage = useMemo(() => {
    if (!packages) return [OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE]

    return {
      label: OPTION_PACKAGE_TYPE.CUSTOM_PACKAGE.label,
      options: packages.map((item: any) => ({
        label: item.name,
        value: {
          length: item.length,
          width: item.width,
          height: item.height,
          dimension_unit: item.dimension_unit,
          package_id: item.id,
          package_type: '',
          package_count: item?.package_count || 1,
        },
      })),
    }
  }, [packages])

  const optionsPackageTypes = useMemo(() => {
    if (isEmpty(carriersPackage)) return [OPTION_PACKAGE_TYPE.CARRIER_STANDARD_PACKAGE]

    return carriersPackage.map((parent: any) => ({
      label: parent.label,
      options: parent?.value.map((item: any) => ({
        label: item.label,
        value: {
          length: item.parcel.length,
          width: item.parcel.width,
          height: item.parcel.height,
          dimension_unit: item.parcel.dimension_unit,
          weight_limit: item.parcel.weight_limit,
          package_type: item.value,
          package_id: null,
          package_count: item?.package_count || 1,
        },
      })),
    }))
  }, [carriersPackage])

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const addPackage = useCallback(() => {
    handleAddNewItem?.('parcels')
  }, [handleAddNewItem])

  const removePackage = useCallback(
    (key: string) => {
      handleDeleteItem?.('parcels', key)
    },
    [handleDeleteItem]
  )

  const onChangeDimensionUnit = useCallback(
    (e: {value: string}) => {
      setCurrentDimensionUnit?.(e.value)

      const parcels = formik.values.parcels
      const firstParcelKey = Object.keys(parcels)?.[0]
      Object.keys(parcels).forEach((key) => {
        formik.setFieldValue(`parcels.${key}.dimension_unit`, e.value)
      })

      const firstParcel = parcels[firstParcelKey]
      const {package_type} = firstParcel || {}
      const {editable, fieldsHidden, packageType} = orderHelper.getPackageDetails(package_type)

      if (editable && fieldsHidden && packageType) {
        ;['length', 'width', 'height'].forEach((field) => {
          if (fieldsHidden.includes(field)) {
            const baseValue = packageType?.parcel?.[field]
            const updatedValue = convertUnit(
              {unit: DIMENSION_UNIT.IN, value: baseValue},
              {unit: e.value}
            ).toString()
            formik.setFieldValue(`parcels.${firstParcelKey}.${field}`, updatedValue)
          }
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.parcels, setCurrentDimensionUnit, orderHelper]
  )

  const onChangeWeightUnit = useCallback(
    (e: {value: string}) => {
      setCurrentWeightUnit?.(e.value)
      const parcels = formik.values.parcels
      Object.keys(parcels).forEach((key) => {
        formik.setFieldValue(`parcels.${key}.weight_unit`, e.value)
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formik.values.parcels, setCurrentWeightUnit]
  )

  const onDimensionChange = useCallback(
    (keyItem: string, dimKey: string, event: any) => {
      if (labelPackageType?.toLowerCase().includes('cubic')) return

      if (isOneRatePackage || oneRateUnmatched) {
        // Guard: carriersPackage may not be loaded yet when this fires.
        if (!oneRateItems.length) return

        const parcel = formik.values.parcels?.[keyItem]
        const dimUnit = parcel?.dimension_unit || DIMENSION_UNIT.IN
        const newVal = Number(event?.target?.value) || 0
        const dims = {
          length: Number(parcel?.length) || 0,
          width: Number(parcel?.width) || 0,
          height: Number(parcel?.height) || 0,
        }
        dims[dimKey] = newVal
        const bestFit = findBestFitOneRate(dims.length, dims.width, dims.height, dimUnit)
        if (bestFit) {
          formik.setFieldValue(`parcels.${keyItem}.package_type`, bestFit.value)
          formik.setFieldValue(
            `parcels.${keyItem}.weight_limit`,
            bestFit.parcel?.weight_limit ?? null
          )
          setOneRateUnmatched(false)
        } else {
          formik.setFieldValue(`parcels.${keyItem}.package_type`, '')
          formik.setFieldValue(`parcels.${keyItem}.package_id`, null)
          setOneRateUnmatched(true)
          // openChoose early-returns when oneRateUnmatched=true, so reset the
          // label here directly so the selector doesn't stay on the old package name.
          setLabelPackageType(packageTypePlaceholder)
        }
        return
      }

      formik.setFieldValue(`parcels.${keyItem}.package_type`, null)
      formik.setFieldValue(`parcels.${keyItem}.package_id`, null)
    },
    // formik.setFieldValue is not referentially stable; omitting `formik` from
    // the dep array is intentional — it would cause an infinite render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      labelPackageType,
      isOneRatePackage,
      oneRateUnmatched,
      oneRateItems,
      formik.values.parcels,
      findBestFitOneRate,
      setLabelPackageType,
      packageTypePlaceholder,
    ]
  )

  const onWeightChange = useCallback(
    (keyItem: string, event: any) => {
      if (labelPackageType?.toLowerCase().includes('cubic')) return

      if (isOneRatePackage || oneRateUnmatched) {
        // Guard: carriersPackage may not be loaded yet when this fires.
        if (!oneRateItems.length) return

        const parcel = formik.values.parcels?.[keyItem]
        const dimUnit = parcel?.dimension_unit || DIMENSION_UNIT.IN

        const bestFit = findBestFitOneRate(
          Number(parcel?.length) || 0,
          Number(parcel?.width) || 0,
          Number(parcel?.height) || 0,
          dimUnit
        )

        if (bestFit) {
          formik.setFieldValue(`parcels.${keyItem}.package_type`, bestFit.value)
          formik.setFieldValue(
            `parcels.${keyItem}.weight_limit`,
            bestFit.parcel?.weight_limit ?? null
          )
          setOneRateUnmatched(false)
        } else {
          formik.setFieldValue(`parcels.${keyItem}.package_type`, '')
          formik.setFieldValue(`parcels.${keyItem}.package_id`, null)
          setOneRateUnmatched(true)
          // openChoose early-returns when oneRateUnmatched=true, so reset the
          // label here directly so the selector doesn't stay on the old package name.
          setLabelPackageType(packageTypePlaceholder)
        }
      }
    },
    // formik.setFieldValue is not referentially stable; omitting `formik` from
    // the dep array is intentional — it would cause an infinite render loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      labelPackageType,
      isOneRatePackage,
      oneRateUnmatched,
      oneRateItems,
      formik.values.parcels,
      findBestFitOneRate,
      setLabelPackageType,
      packageTypePlaceholder,
    ]
  )

  // ---------------------------------------------------------------------------
  // Select styling helper
  // ---------------------------------------------------------------------------

  const customSelectInputStyle = useCallback((isDisabled?: boolean) => {
    const color = isDisabled ? '#a1a5b7' : '#0B6D65'
    return {
      styleInput: {
        background: 'transparent',
        width: '80px',
        backgroundImage: 'none',
        borderColor: 'transparent',
        '&:hover': {borderColor: 'transparent', background: '#F0FDFA'},
        color,
        textAlign: 'right',
      },
      styleDropdownIndicator: {
        color,
        '&:hover': {color},
        padding: 0,
        marginRight: 0,
      },
      customMenuPortal: {marginTop: '-14px'},
    }
  }, [])

  return {
    // state
    labelPackageType,
    setLabelPackageType,
    oneRateUnmatched,
    isDisabledPackageCount,
    isDisabledDimension,
    isDisabledAddPackage,
    reachLimitRow,
    parcelsCount,
    carriersPackage,

    // data
    packageTypePlaceholder,
    optionsPackageTypes,
    optionsCustomPackage,
    totalWeightPackage,
    totalPackages,
    isOneRatePackage,
    weightLimitError,
    setOneRateUnmatched,

    // handlers
    addPackage,
    removePackage,
    onChangeDimensionUnit,
    onChangeWeightUnit,
    onDimensionChange,
    onWeightChange,
    customSelectInputStyle,
  }
}
